import {
  useState,
  createContext,
  useMemo,
  useContext,
  PropsWithChildren,
  RefObject,
  useRef,
  useEffect,
} from 'react';

const defaultRefs = {};

type RefsContextType = {
  refs: Record<string, RefObject<any>>,
  addRef: (id: string, ref: RefObject<HTMLElement>) => void,
}

export const RefsContext = createContext<RefsContextType>({
  refs: defaultRefs,
  addRef: () => { },
});

export function useRefs() {
  return useContext(RefsContext);
}

export function useCreateAndAddRefs(id: string) {
  const { addRef, refs } = useRefs();

  const ref = useRef(null);

  useEffect(() => {
    addRef(id, ref);
  }, [ref, id]);

  return refs?.[id] ?? ref;
}

export function RefsProvider({ children }: PropsWithChildren) {
  const [refs, setRefs] = useState<Record<string, RefObject<HTMLElement>>>(defaultRefs);

  const addRef = (id: string, ref: RefObject<HTMLElement>) => {
    if (!refs?.[id]) {
      setRefs((prev) => ({ ...prev, [id]: ref }));
    }
  };

  const refsValue = useMemo(() => ({
    refs,
    addRef,
  }), [refs, addRef]);

  return (
    <RefsContext.Provider value={refsValue}>
      {children}
    </RefsContext.Provider>
  );
}
